/**
 * src/lib/auth.js — đăng nhập bằng liên kết gửi qua email (magic link).
 *
 * ─────────────────────────────────────────────────────────────────────────
 * VÌ SAO KHÔNG DÙNG MẬT KHẨU: tài khoản ở đây chỉ để đồng bộ Bản chuẩn bị
 * giữa máy tính và điện thoại. Thêm mật khẩu nghĩa là thêm màn hình quên mật
 * khẩu, thêm rủi ro người dùng đặt lại mật khẩu họ đang dùng ở chỗ khác, và
 * thêm một thứ nữa để rò rỉ. Liên kết qua email bỏ được toàn bộ phần đó.
 *
 * NGUYÊN TẮC KHÔNG ĐƯỢC PHÁ: đăng nhập là TUỲ CHỌN. Mọi công cụ phải chạy
 * đầy đủ khi chưa đăng nhập và khi chưa cấu hình Firebase. Không bao giờ chặn
 * kết quả sau màn hình đăng nhập — phần lớn người dùng đến từ Google và sẽ
 * rời đi ngay nếu bị hỏi email trước khi thấy giá trị.
 * ─────────────────────────────────────────────────────────────────────────
 */

import { track } from './track.js';
import { CO_CAU_HINH, layApp } from './firebase.js';

/** Email đang chờ xác nhận. Firebase cần lại địa chỉ khi người dùng bấm link. */
const KHOA_EMAIL = 'fundraising-email-dang-cho';

let authPromise = null;

/** Nạp Firebase Auth theo kiểu lười — người không đăng nhập không phải tải. */
export function layAuth() {
  if (!CO_CAU_HINH) return Promise.resolve(null);
  if (!authPromise) {
    authPromise = (async () => {
      // Dùng chung một app với Firestore/Analytics (src/lib/firebase.js), để
      // cấu hình — kể cả measurementId — chỉ khai ở một nơi.
      const [app, auth] = await Promise.all([layApp(), import('firebase/auth')]);
      return { auth: auth.getAuth(app), sdk: auth };
    })();
  }
  return authPromise;
}

/** Kiểm tra thô ở phía trình duyệt để báo lỗi sớm; Firebase mới là bên quyết định. */
export function emailHopLe(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(email || '').trim());
}

/**
 * Gửi liên kết đăng nhập. Người dùng bấm link trong hộp thư và quay lại đúng
 * trang này, nên họ không mất ngữ cảnh đang làm dở.
 */
export async function guiLienKet(email, quayVe = '/ban-chuan-bi') {
  const dc = String(email || '').trim().toLowerCase();
  if (!emailHopLe(dc)) throw new Error('Địa chỉ email chưa đúng định dạng.');
  const x = await layAuth();
  if (!x) throw new Error('Website chưa cấu hình đăng nhập.');
  await x.sdk.sendSignInLinkToEmail(x.auth, dc, {
    url: `${window.location.origin}${quayVe}`,
    handleCodeInApp: true,
  });
  // Lưu để không phải hỏi lại email khi họ quay lại. Nếu họ mở link trên thiết
  // bị khác thì không có sẵn ở đây, lúc đó mới hỏi.
  try { window.localStorage.setItem(KHOA_EMAIL, dc); } catch { /* chế độ riêng tư */ }
  return dc;
}

/** Địa chỉ đã lưu lúc bấm nút gửi, nếu vẫn còn. */
export function emailDangCho() {
  try { return window.localStorage.getItem(KHOA_EMAIL) || ''; } catch { return ''; }
}

/** URL hiện tại có phải liên kết đăng nhập không. */
export async function laLienKetDangNhap() {
  const x = await layAuth();
  if (!x) return false;
  return x.sdk.isSignInWithEmailLink(x.auth, window.location.href);
}

/**
 * Hoàn tất đăng nhập từ liên kết. Trả về user, hoặc ném lỗi có câu tiếng Việt
 * đọc được — thông báo gốc của Firebase là tiếng Anh và khó hiểu.
 */
export async function hoanTatDangNhap(email) {
  const x = await layAuth();
  if (!x) throw new Error('Website chưa cấu hình đăng nhập.');
  const dc = String(email || emailDangCho()).trim().toLowerCase();
  if (!emailHopLe(dc)) throw new Error('Cần nhập lại email đã dùng để nhận liên kết.');
  try {
    const kq = await x.sdk.signInWithEmailLink(x.auth, dc, window.location.href);
    try { window.localStorage.removeItem(KHOA_EMAIL); } catch { /* bỏ qua */ }
    // Xoá tham số đăng nhập khỏi thanh địa chỉ: liên kết dùng một lần, để lại
    // chỉ khiến người dùng chia sẻ nhầm hoặc bấm lại rồi gặp lỗi.
    // Giữ nguyên trang người dùng quay về (Bản chuẩn bị, hoặc trang gửi deck).
    window.history.replaceState({}, '', window.location.pathname);
    track('login_complete', { method: 'email' });
    return kq.user;
  } catch (e) {
    const ma = e?.code || '';
    if (ma.includes('invalid-action-code') || ma.includes('expired'))
      throw new Error('Liên kết đã hết hạn hoặc đã dùng rồi. Hãy gửi lại liên kết mới.');
    if (ma.includes('invalid-email'))
      throw new Error('Email không khớp với địa chỉ đã nhận liên kết.');
    throw new Error(e?.message || 'Không đăng nhập được.');
  }
}

/**
 * Dấu "đã đăng nhập" trong trình duyệt, chỉ để đầu trang đổi chữ "Đăng nhập"
 * thành "Bản chuẩn bị" mà không phải tải Firebase Auth trên mọi trang.
 * Không chứa thông tin gì về người dùng; sai lệch tạm thời cũng vô hại vì cả
 * hai chữ đều dẫn tới cùng một chỗ.
 */
export const KHOA_DAU_DANG_NHAP = 'fr-da-dang-nhap';
export function ghiDauDaDangNhap(co) {
  try {
    if (co) localStorage.setItem(KHOA_DAU_DANG_NHAP, '1');
    else localStorage.removeItem(KHOA_DAU_DANG_NHAP);
    window.dispatchEvent(new Event('fr-dang-nhap'));
  } catch { /* bộ nhớ bị chặn: bỏ qua */ }
}
export function coDauDaDangNhap() {
  try { return localStorage.getItem(KHOA_DAU_DANG_NHAP) === '1'; } catch { return false; }
}

/** Theo dõi trạng thái đăng nhập. Trả về hàm huỷ theo dõi. */
export function theoDoiDangNhap(goi) {
  if (!CO_CAU_HINH) { goi(null); return () => {}; }
  let huy = () => {};
  let daHuy = false;
  layAuth().then((x) => {
    if (!x || daHuy) return;
    huy = x.sdk.onAuthStateChanged(x.auth, (u) => { ghiDauDaDangNhap(Boolean(u)); goi(u || null); });
  }).catch(() => goi(null));
  return () => { daHuy = true; huy(); };
}

/**
 * Đăng nhập bằng tài khoản Google.
 *
 * Vì sao có thêm cách này bên cạnh liên kết email: gần như người dùng Việt Nam
 * nào cũng có sẵn Gmail, bấm một lần là xong, không phải rời trang đi mở hộp
 * thư rồi quay lại. Liên kết email vẫn giữ cho ai dùng email công ty.
 *
 * Thử cửa sổ bật lên trước; trình duyệt trong ứng dụng (Zalo, Facebook) và một
 * số trình duyệt di động chặn cửa sổ bật lên, nên khi đó chuyển sang chuyển
 * hướng cả trang. Kết quả chuyển hướng được đọc lại bằng ketQuaChuyenHuong().
 */
export async function dangNhapGoogle() {
  const x = await layAuth();
  if (!x) throw new Error('Website chưa cấu hình đăng nhập.');
  const provider = new x.sdk.GoogleAuthProvider();
  // Luôn hỏi chọn tài khoản: nhiều người có cả Gmail cá nhân và email công ty,
  // im lặng đăng nhập nhầm tài khoản thì Bản chuẩn bị của họ "biến mất".
  provider.setCustomParameters({ prompt: 'select_account' });
  try {
    const kq = await x.sdk.signInWithPopup(x.auth, provider);
    track('login_complete', { method: 'google' });
    return kq.user;
  } catch (e) {
    const ma = e?.code || '';
    if (ma.includes('popup-blocked') || ma.includes('operation-not-supported')
      || ma.includes('cancelled-popup-request')) {
      await x.sdk.signInWithRedirect(x.auth, provider);
      return null; // trang sẽ tải lại; kết quả đọc ở lần tải sau
    }
    // Người dùng tự đóng cửa sổ không phải lỗi.
    if (ma.includes('popup-closed-by-user')) return null;
    if (ma.includes('operation-not-allowed'))
      throw new Error('Đăng nhập Google chưa được bật trong Firebase Console.');
    if (ma.includes('unauthorized-domain'))
      throw new Error('Tên miền này chưa được khai trong Authorized domains của Firebase.');
    throw new Error(e?.message || 'Không đăng nhập được bằng Google.');
  }
}

/** Đọc kết quả sau khi quay lại từ luồng chuyển hướng. Không có thì trả về null. */
export async function ketQuaChuyenHuong() {
  const x = await layAuth();
  if (!x) return null;
  try {
    const kq = await x.sdk.getRedirectResult(x.auth);
    return kq?.user || null;
  } catch {
    return null;
  }
}

export async function dangXuat() {
  const x = await layAuth();
  if (x) await x.sdk.signOut(x.auth);
  ghiDauDaDangNhap(false);
}

/**
 * Xoá tài khoản. Firebase yêu cầu đăng nhập gần đây mới cho xoá; nếu phiên đã
 * cũ thì phải gửi lại liên kết. Việc xoá dữ liệu trên Firestore do bên gọi lo
 * TRƯỚC khi gọi hàm này — mất quyền rồi thì không xoá được nữa.
 */
/** Phiên đăng nhập còn "tươi" không — Firebase chỉ cho xoá tài khoản khi vừa
 *  đăng nhập gần đây. */
export async function phienConTuoi(phut = 5) {
  const x = await layAuth();
  const t = Date.parse(x?.auth?.currentUser?.metadata?.lastSignInTime || '');
  return Number.isFinite(t) && Date.now() - t < phut * 60 * 1000;
}

export async function xoaTaiKhoan() {
  const x = await layAuth();
  const u = x?.auth?.currentUser;
  if (!u) throw new Error('Chưa đăng nhập.');
  try {
    await u.delete();
  } catch (e) {
    if (String(e?.code || '').includes('requires-recent-login'))
      throw new Error('Vì lý do an toàn, hãy đăng nhập lại bằng liên kết mới rồi xoá tài khoản.');
    throw e;
  }
}
